import {
  Box,
  CopyToClipboard,
  DescriptionList,
  type DescriptionListProps,
  formatNumber,
  Inline,
  Typography,
  type TypographyProps,
} from '@omnipresentgroup/design-system';
import {
  useContractorPayment,
  useGetContractorInvoice,
} from '@omnipresentgroup/domains';
import { objectValuesToString } from '@omnipresentgroup/helpers';
import type { ContractorPayment } from '@omnipresentgroup/types';

import { BankDetailsSkeleton } from './components';

const TOTAL_AMOUNT_LABEL = 'Amount';
const ACCOUNT_NAME_LABEL = 'Account name';
const IBAN_LABEL = 'IBAN';
const ACCOUNT_NUMBER = 'Account number';
const SORT_CODE = 'Sort code';
const BIC_SWIFT_LABEL = 'BIC/SWIFT';
const REFERENCE_DESCRIPTION_LABEL = 'Reference/description';
const ADDRESS_LABEL = 'Address';

const DescriptionListValue: React.FC<
  {
    children: string;
  } & Omit<TypographyProps<'span'>, 'as' | 'size'>
> = ({ children, ...typographyOptions }) => (
  <Typography as="span" size="14" {...typographyOptions}>
    {children}
  </Typography>
);

type Props = Pick<DescriptionListProps, 'border'> &
  (
    | {
        invoiceId?: never;
        payment: ContractorPayment;
      }
    | { invoiceId: string; payment?: never }
  );

export const ContractorInvoiceBankTransferDetails = ({
  invoiceId,
  payment,
  ...descriptionListProps
}: Props) => {
  const { data: invoice } = useGetContractorInvoice({
    id: invoiceId,
    enabled: Boolean(invoiceId) && !payment,
  });

  const { data: paymentData, isLoading: isPaymentDataLoading } =
    useContractorPayment({
      id: invoice?.paymentId,
      enabled: !!invoice?.paymentId,
    });

  const { transferDetails, quote, id } = payment || paymentData || {};

  const transferDetailsList: {
    label: string;
    value: string | JSX.Element;
    copyText?: string;
  }[] =
    transferDetails && quote
      ? [
          {
            label: TOTAL_AMOUNT_LABEL,
            value: (
              <DescriptionListValue weight="medium">
                {formatNumber({
                  input: Number(quote.quoteSummary.transferAmount.amount),
                  currency: quote.quoteSummary.transferAmount.currency,
                })}
              </DescriptionListValue>
            ),
            copyText: String(quote.quoteSummary.transferAmount.amount),
          },
          {
            label: REFERENCE_DESCRIPTION_LABEL,
            value: (
              <DescriptionListValue weight="medium">
                {transferDetails.wireReference}
              </DescriptionListValue>
            ),
            copyText: transferDetails.wireReference,
          },
          {
            label: ACCOUNT_NAME_LABEL,
            value: transferDetails.bankAccount.ownerName,
          },
          ...(transferDetails.bankAccount.bankAccountType === 'IBAN'
            ? [
                {
                  label: IBAN_LABEL,
                  value: transferDetails.bankAccount.iban,
                },
                ...(transferDetails.bankAccount.swiftBic
                  ? [
                      {
                        label: BIC_SWIFT_LABEL,
                        value: transferDetails.bankAccount.swiftBic,
                      },
                    ]
                  : []),
              ]
            : []),
          ...(transferDetails.bankAccount.bankAccountType === 'GB'
            ? [
                {
                  label: ACCOUNT_NUMBER,
                  value: transferDetails.bankAccount.accountNumber,
                },
                {
                  label: SORT_CODE,
                  value: transferDetails.bankAccount.sortCode,
                },
              ]
            : []),
          {
            label: ADDRESS_LABEL,
            value: transferDetails?.bankAccount.ownerAddress
              ? objectValuesToString(transferDetails.bankAccount.ownerAddress)
              : '-',
          },
        ]
      : [];

  const list = transferDetailsList.map(({ label, value, copyText }) => ({
    header: label,
    detail: (
      <Inline justify="space-between" align="center">
        <Box overflow="auto">
          {typeof value === 'string' ? (
            <Typography size="14" as="span">
              {value}
            </Typography>
          ) : (
            value
          )}
        </Box>
        <CopyToClipboard textToCopy={copyText || String(value)} />
      </Inline>
    ),
  }));

  return !payment && isPaymentDataLoading ? (
    <BankDetailsSkeleton />
  ) : (
    <DescriptionList
      {...descriptionListProps}
      list={list}
      id={`transfer-details-${id}`}
    />
  );
};
